import { useCallback, useRef, useState } from 'react';
import {
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { TriangleDownIcon, CheckIcon, CloseIcon, SearchIcon } from '@chakra-ui/icons';

export interface FilterOption {
  key: string;
  label: string;
  isSelected: boolean;
}

interface FilterMenuItemProps {
  option: FilterOption;
  onClick: (key: string) => void;
}

function FilterMenuItem({ option, onClick }: FilterMenuItemProps) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const handleMouseEnter = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  return (
    <Tooltip
      label={option.label}
      placement="right"
      isDisabled={!isTruncated}
      openDelay={300}
      fontSize="12px"
      borderRadius="5px"
      textAlign="center"
      p={1}
      zIndex={1000}
      closeOnScroll
    >
      <MenuItem
        fontSize="13px"
        textStyle="bodyFamilyRegular"
        color={option.isSelected ? '#505E69' : '#4C4C4C'}
        bg="#F8F8F8"
        _hover={{ bg: '#FFFFFF' }}
        _focus={{ bg: '#F8F8F8', _hover: { bg: '#FFFFFF' } }}
        onClick={() => onClick(option.key)}
        onMouseEnter={handleMouseEnter}
        fontWeight={option.isSelected ? 600 : 400}
        justifyContent="space-between"
      >
        <Text ref={textRef} isTruncated flex="1" title="">
          {option.label}
        </Text>
        {option.isSelected && (
          <CheckIcon boxSize="10px" flexShrink={0} ml="8px" />
        )}
      </MenuItem>
    </Tooltip>
  );
}

const menuListProps = {
  minW: 'unset',
  w: 'full',
  borderTopRadius: 0,
  borderBottomRadius: '5px',
  border: 'none',
  boxShadow: 'md',
  py: 0,
  overflow: 'hidden' as const,
};

interface SearchableDropdownButtonProps {
  buttonLabel: string;
  isActive: boolean;
  onClear?: () => void;
  clearAriaLabel?: string;
  minW?: string;
  maxButtonLabelW?: string;
  closeOnSelect?: boolean;
  options: FilterOption[];
  onOptionClick: (key: string) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onMenuClose?: () => void;
  emptyMessage?: string;
  loadMoreRef?: React.RefObject<HTMLDivElement>;
  showLoadMore?: boolean;
}

function SearchableDropdownButton({
  buttonLabel,
  isActive,
  onClear,
  clearAriaLabel,
  minW = '100px',
  maxButtonLabelW = '200px',
  closeOnSelect = false,
  options,
  onOptionClick,
  searchValue,
  onSearchChange,
  onMenuClose,
  emptyMessage = 'No results found',
  loadMoreRef,
  showLoadMore,
}: SearchableDropdownButtonProps) {
  const searchable = onSearchChange !== undefined;

  return (
    <Menu
      placement="bottom-start"
      closeOnSelect={closeOnSelect}
      gutter={0}
      matchWidth
      onClose={onMenuClose}
    >
      <Flex align="center" position="relative">
        <MenuButton
          as={Button}
          variant="outline"
          size="sm"
          borderRadius="5px"
          bg="white"
          fontWeight={400}
          color={isActive ? '#505E69' : '#4C4C4C'}
          border={isActive ? '1.5px solid #505E69' : 'none'}
          _hover={{ borderColor: '#505E69' }}
          _expanded={{ borderBottomRadius: 0, bg: '#F8F8F8' }}
          pr={onClear && isActive ? '43px' : '28px'}
          minW={minW}
        >
          <Text
            textTransform="none"
            letterSpacing="0.01em"
            isTruncated
            maxW={maxButtonLabelW}
          >
            {buttonLabel}
          </Text>
        </MenuButton>
        {onClear && isActive && (
          <CloseIcon
            aria-label={clearAriaLabel}
            boxSize="9px"
            position="absolute"
            right="28px"
            cursor="pointer"
            zIndex={1}
            color="#989898"
            _hover={{ color: '#4C4C4C' }}
            onClick={onClear}
          />
        )}
        <TriangleDownIcon
          boxSize="10px"
          position="absolute"
          right="10px"
          pointerEvents="none"
        />
      </Flex>
      <MenuList
        {...menuListProps}
        maxH={searchable ? '290px' : '250px'}
        overflow={searchable ? 'hidden' : 'auto'}
      >
        {searchable && (
          <InputGroup
            position="sticky"
            top={0}
            zIndex={1}
            bg="#F8F8F8"
            borderBottom="1px solid #E4E4E4"
          >
            <InputLeftElement
              h="30px"
              w="30px"
              pointerEvents="none"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <SearchIcon boxSize="10px" color="#989898" />
            </InputLeftElement>
            <Input
              placeholder="Search"
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              h="30px"
              pl="30px"
              fontSize="13px"
              border="none"
              borderRadius={0}
              bg="#F8F8F8"
              _hover={{ bg: '#F8F8F8' }}
              _focus={{ bg: '#F8F8F8', boxShadow: 'none' }}
              _focusVisible={{ bg: '#F8F8F8', boxShadow: 'none' }}
              _active={{ bg: '#F8F8F8', boxShadow: 'none' }}
            />
          </InputGroup>
        )}
        <Flex direction="column" maxH={searchable ? '250px' : undefined} overflowY={searchable ? 'auto' : undefined}>
          {options.map((option) => (
            <FilterMenuItem
              key={option.key}
              option={option}
              onClick={onOptionClick}
            />
          ))}
          {searchable && options.length === 0 && (
            <Text fontSize="13px" color="#989898" px="12px" py="8px">
              {emptyMessage}
            </Text>
          )}
          {showLoadMore && <div ref={loadMoreRef} style={{ height: 1 }} />}
        </Flex>
      </MenuList>
    </Menu>
  );
}

export default SearchableDropdownButton;
